import React, { useState } from "react";
import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ModelDetailModal from "./ModelDetailModal";
import { DEFAULT_DELOITTE_OVERAGE_RATE } from "./useDashboardSettings";

const DISCOUNT_GREEN = "#26890d";
const OVERAGE_RED = "#E2231A";
const OVERAGE_COLUMN_BG = "#86bc254f";
const USAGE_RED = "#E2231A";
const USAGE_ORANGE = "#F28C00";
const USAGE_YELLOW = "#FFCD00";

// Deloitte's negotiated overage rate vs the $0.01/AIC list price used to bill
// "Overage" — the difference is the Deloitte-specific discount. The rate itself
// is configurable (dashboard settings), not hardcoded — see deloitteOverageRate prop.
const LIST_OVERAGE_RATE = 0.01;

// Provisional cutoffs based on one sample dataset, not a derived/validated distribution.
// Revisit once a few weeks of real org usage data is available.
const USAGE_RED_THRESHOLD_PCT = 25;
const USAGE_ORANGE_THRESHOLD_PCT = 15;

const getUsageColor = (percentOfTotal) => {
  if (percentOfTotal >= USAGE_RED_THRESHOLD_PCT) return USAGE_RED;
  if (percentOfTotal >= USAGE_ORANGE_THRESHOLD_PCT) return USAGE_ORANGE;
  return USAGE_YELLOW;
};

const headerCellSx = {
  fontWeight: 700,
  fontSize: "0.78rem",
  color: "#555",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  backgroundColor: "#fafafa",
  borderBottom: "2px solid #e8e8e8",
  py: 1.5,
};

const wrappedHeaderCellSx = {
  ...headerCellSx,
  whiteSpace: "pre-line",
  lineHeight: 1.4,
};

const bodyCellSx = {
  fontSize: "0.85rem",
  color: "#1a1a1a",
  py: 1.2,
  borderBottom: "1px solid #f0f0f0",
};

const formatAIC = (v) => v.toLocaleString("en-AU", { maximumFractionDigits: 2 });
const formatSpend = (v) =>
  "$" + v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// API returns models pre-sorted desc by netQuantity, each with percentOfTotal precomputed;
// fall back to client-side sort/percent for responses that predate that shape.
const hasPercentOfTotal = (models) => models.length === 0 || models[0].percentOfTotal !== undefined;

// The API's percentOfTotal is share of net (post-discount/overage) AIC, not gross.
// When an org is heavily or fully pool-covered, netQuantity — and therefore
// percentOfTotal — is 0 for every model simultaneously (confirmed: an org at
// pool_utilization_pct 100 sends percentOfTotal: 0.0 on every row), which would
// render every bar as empty even though gross usage varies a lot between models.
// The bar's job is to show usage weight, so it's computed here from grossQuantity
// instead, independent of percentOfTotal's own (net-based) denominator.
const getGrossSharePct = (row, totalGrossAIC) =>
  totalGrossAIC > 0 ? ((row.grossQuantity ?? 0) / totalGrossAIC) * 100 : 0;

const ModelTable = ({
  models,
  totalOverageCredits,
  totalOverageAmount,
  deloitteOverageRate,
  orgId,
  costCenterId,
  platformId,
  fromDate,
  toDate,
  isEnterprise = false,
}) => {
  const [selectedModel, setSelectedModel] = useState(null);

  const sortedModels = hasPercentOfTotal(models)
    ? models
    : [...models].sort((a, b) => (b.netQuantity ?? 0) - (a.netQuantity ?? 0));

  const totals = sortedModels.reduce(
    (acc, m) => ({
      grossQuantity: acc.grossQuantity + (m.grossQuantity ?? 0),
      grossAmount: acc.grossAmount + (m.grossAmount ?? 0),
      discountAmount: acc.discountAmount + (m.discountAmount ?? 0),
    }),
    { grossQuantity: 0, grossAmount: 0, discountAmount: 0 }
  );
  // Uses the API's own org-level totals (same source as the "Overage Billed" and
  // "Cost after Deloitte Discount" stat cards) instead of summing per-model
  // amounts — keeps the footer totals exactly in sync with the stat cards
  // rather than drifting from floating-point rounding accumulated across rows.
  const effectiveDeloitteRate = deloitteOverageRate ?? DEFAULT_DELOITTE_OVERAGE_RATE;
  const totalOverage = totalOverageAmount ?? 0;
  const totalDeloitteCost = (totalOverageCredits ?? 0) * effectiveDeloitteRate;

  const handleRowClick = (row) => {
    setSelectedModel(row);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 0,
        border: "none",
        backgroundColor: "#fff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1a1a1a" }}>
          Consumption by Model
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "#999", mt: 0.3 }}>
          GitHub Copilot usage-based billing - ( All models )
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer sx={{ minHeight: 240, maxHeight: 560, overflowY: "auto", overflowX: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 48 }}>#</TableCell>
              <TableCell sx={headerCellSx}>Model</TableCell>
              <TableCell sx={headerCellSx} align="right">Gross AIC</TableCell>
              <TableCell sx={headerCellSx} align="right">Gross Amount</TableCell>
              <TableCell sx={headerCellSx} align="right">Pool Covered ($)</TableCell>
              <TableCell sx={wrappedHeaderCellSx} align="right">{"Overage\nBilled"}</TableCell>
              <TableCell sx={wrappedHeaderCellSx} align="right">{"Overage\nDiscount"}</TableCell>
              <TableCell sx={wrappedHeaderCellSx} align="right">{"Net Overage\nCost"}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedModels.map((row, idx) => {
              const usagePercent = getGrossSharePct(row, totals.grossQuantity);
              const usageColor = getUsageColor(usagePercent);
              return (
                <TableRow
                  key={row.model}
                  onClick={() => handleRowClick(row)}
                  sx={{
                    "&:hover": { backgroundColor: "#eef6ff", cursor: "pointer" },
                    "&:last-child td": { border: 0 },
                  }}
                >
                  <TableCell sx={{ ...bodyCellSx, color: "#999", width: 48 }}>
                    {idx + 1}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Box>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#1a1a1a" }}>
                        {row.model}
                      </Typography>
                      <Tooltip title={`${usagePercent.toFixed(1)}% of total Gross AIC`} placement="right" arrow>
                        <LinearProgress
                          variant="determinate"
                          value={usagePercent}
                          sx={{
                            mt: 0.5,
                            height: 4,
                            borderRadius: 4,
                            backgroundColor: "#f0f0f0",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: usageColor,
                              borderRadius: 4,
                            },
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">
                    {formatAIC(row.grossQuantity)}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">
                    {formatSpend(row.grossAmount)}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: DISCOUNT_GREEN }} align="right">
                    {formatSpend(row.discountAmount)}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: OVERAGE_RED }} align="right">
                    {formatSpend(row.netAmount)}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: DISCOUNT_GREEN, backgroundColor: OVERAGE_COLUMN_BG }} align="right">
                    {formatSpend(
                      row.overageDiscountAmount ??
                        (row.netQuantity ?? 0) * (LIST_OVERAGE_RATE - effectiveDeloitteRate)
                    )}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: OVERAGE_RED, backgroundColor: OVERAGE_COLUMN_BG }} align="right">
                    {formatSpend(
                      row.netOverageCostAmount ?? (row.netQuantity ?? 0) * effectiveDeloitteRate
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter sx={{ position: "sticky", bottom: 0, zIndex: 2 }}>
            <TableRow sx={{ backgroundColor: "#fafafa" }}>
              <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", width: 48 }} />
              <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", fontWeight: 700, color: "#1a1a1a" }}>
                Total
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", fontWeight: 700 }} align="right">
                {formatAIC(totals.grossQuantity)}
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", fontWeight: 700 }} align="right">
                {formatSpend(totals.grossAmount)}
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", fontWeight: 700, color: DISCOUNT_GREEN }} align="right">
                {formatSpend(totals.discountAmount)}
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", fontWeight: 700, color: OVERAGE_RED }} align="right">
                {formatSpend(totalOverage)}
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: OVERAGE_COLUMN_BG, fontWeight: 700, color: DISCOUNT_GREEN }} align="right">
                {formatSpend(totalOverage - totalDeloitteCost)}
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: OVERAGE_COLUMN_BG, fontWeight: 700, color: OVERAGE_RED }} align="right">
                {formatSpend(totalDeloitteCost)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <ModelDetailModal
        modelName={selectedModel?.model}
        modelStats={selectedModel}
        open={Boolean(selectedModel)}
        onClose={() => setSelectedModel(null)}
        orgId={orgId}
        costCenterId={costCenterId}
        platformId={platformId}
        fromDate={fromDate}
        toDate={toDate}
        isEnterprise={isEnterprise}
      />
    </Paper>
  );
};

export default ModelTable;
